import { serverGenerates } from "../services/execution.js";
import { store } from "../state/store.js";
/**
 * ExecutionPicker — choix de la machine de génération : cet ordinateur ou
 * l'une des machines pairées en ligne. Visible seulement quand un compte
 * est connecté et qu'il y a au moins deux machines entre lesquelles choisir.
 */
import { h } from "./dom.js";
import { formatGb } from "./format.js";

export function executionPicker(): HTMLElement {
	const select = h("select") as HTMLSelectElement;
	const wrapper = h(
		"div",
		{ class: "form-group" },
		h("label", { class: "field-label" }, "Générer sur"),
		select
	);

	function render(): void {
		const { workers, user } = store.get();
		// "Cet ordinateur" means the server this page came from. The hosted
		// site has no GPU, so there it is not a choice at all -- offering it,
		// preselected, sent every public generation to a 404.
		const local = serverGenerates();
		// Heard from last first, as the server picks the machine it describes
		// (routes/system.paired_machine): a program just closed or re-paired
		// stays « online » for the heartbeat timeout, and preselecting it
		// estimated the image on a machine that no longer answered.
		const online = workers
			.filter((w) => w.online && w.mode === "local")
			.sort((a, b) => Date.parse(b.observed_at) - Date.parse(a.observed_at));
		const options: { value: string; label: string }[] = [
			...(local ? [{ value: "local", label: "Cet ordinateur" }] : []),
			...online.map((w) => ({
				value: w.worker_id,
				label: `Mon ordinateur — ${w.gpu_name ?? w.device_type} (${formatGb(w.vram_gb, 0)})`,
			})),
		];

		const current = store.get().executionTarget;
		const currentId = current.kind === "hosted" ? current.worker.worker_id : "local";
		const chosen = options.some((o) => o.value === currentId) ? currentId : options[0]?.value;
		if (chosen !== currentId) {
			const worker = online.find((w) => w.worker_id === chosen);
			store.setState({
				executionTarget: worker ? { kind: "hosted", worker } : { kind: "local" },
			});
		}

		// A choice only when there is one to make: with a single computer the
		// target is set above all the same, and the field only repeated the
		// top bar's « <GPU> est prêt » in every workspace.
		const show = Boolean(user) && options.length > 1;
		wrapper.classList.toggle("hidden", !show);
		if (!show) return;
		select.replaceChildren(...options.map((o) => h("option", { value: o.value }, o.label)));
		select.value = chosen ?? "";
	}

	select.addEventListener("change", () => {
		const worker = store.get().workers.find((w) => w.worker_id === select.value);
		store.setState({
			executionTarget:
				worker && select.value !== "local" ? { kind: "hosted", worker } : { kind: "local" },
		});
	});

	store.subscribe((s) => s.workers, render);
	store.subscribe((s) => s.user, render);
	store.subscribe((s) => s.status?.generates_here ?? null, render);
	render();

	return wrapper;
}
